import { useTranslation } from 'react-i18next'
import { CodeBlock } from '@mysaiva/core'
import type { DocResource, DocTable as DocTableModel } from '../../docs'
import { markdownToBlocks } from '../../docs/markdown'
import { DocBlocks } from './DocBlocks'
import { DocLayout } from './DocLayout'
import type { DocAnchor } from './DocLayout'
import { DocTable } from './DocTable'
import styles from './Docs.module.scss'

// Parameter tables in page order: anchor suffix, label key, resource field.
const PARAMETER_GROUPS = [
  { id: 'path-parameters', label: 'pathParameters', key: 'pathParameters' },
  { id: 'query-parameters', label: 'queryParameters', key: 'queryParameters' },
  { id: 'request-headers', label: 'requestHeaders', key: 'headerParameters' }
] as const
const RESPONSE_FIELDS_ID = 'response-fields'
const RESPONSE_HEADERS_ID = 'response-headers'

interface DocResourceViewProps {
  /** DOM id of the article, the scroll target on continuous pages. */
  id?: string
  resource: DocResource
  title: string
  'data-testid'?: string
}

/**
 * Renders one API resource (description, query parameters, response fields,
 * response headers; request and response examples in the code column) in the documentation page layout, from a `DocResource`
 * generated from the OpenAPI document.
 */
export function DocResourceView({
  id,
  resource,
  title,
  'data-testid': testId = `dp-doc-resource-${resource.id}`
}: DocResourceViewProps) {
  const { t } = useTranslation()
  const ui = (key: string) => t(`developerPlatform.docs.ui.${key}`)

  // Several resources share a page, so the anchors carry the resource id.
  const fieldsId = `${resource.id}-${RESPONSE_FIELDS_ID}`
  const headersId = `${resource.id}-${RESPONSE_HEADERS_ID}`
  const parameterTables = PARAMETER_GROUPS.flatMap(group => {
    const params = resource[group.key]
    if (!params?.length) return []
    const table: DocTableModel = {
      columns: [ui('parameter'), ui('type'), ui('required'), ui('description')],
      rows: params.map(p => [
        { code: p.name },
        p.type,
        p.required ? ui('yes') : ui('no'),
        p.description
      ])
    }
    return [
      {
        id: `${resource.id}-${group.id}`,
        label: ui(group.label),
        testId: `dp-doc-${group.id}`,
        table
      }
    ]
  })
  const anchors: DocAnchor[] = parameterTables.map(({ id, label }) => ({ id, label }))
  if (resource.responseFields?.length) {
    anchors.push({ id: fieldsId, label: ui('responseFields') })
  }
  if (resource.responseHeaders?.length) {
    anchors.push({ id: headersId, label: ui('responseHeaders') })
  }

  const fields: DocTableModel | null = resource.responseFields?.length
    ? {
        columns: [ui('field'), ui('type'), ui('required'), ui('description')],
        rows: resource.responseFields.map(f => [
          { code: f.name },
          f.nullable ? `${f.type} | null` : f.type,
          f.required ? ui('yes') : ui('no'),
          f.description
        ])
      }
    : null

  const headers: DocTableModel | null = resource.responseHeaders?.length
    ? {
        columns: [ui('header'), ui('type'), ui('description')],
        rows: resource.responseHeaders.map(h => [{ code: h.name }, h.type, h.description])
      }
    : null

  return (
    <DocLayout
      id={id}
      title={title}
      draft={resource.draft}
      anchors={anchors}
      titleExtra={
        <span className={styles.endpoint} data-testid="dp-doc-endpoint">
          <span className={styles.method}>{resource.method}</span>
          {resource.path}
        </span>
      }
      main={
        <>
          <DocBlocks blocks={markdownToBlocks(resource.description)} />
          {parameterTables.map(({ id, label, testId, table }) => (
            <section key={id} className={styles.section} id={id}>
              <h2 className={styles.sectionTitle}>{label}:</h2>
              <DocTable table={table} data-testid={testId} />
            </section>
          ))}
          {fields && (
            <section className={styles.section} id={fieldsId}>
              <h2 className={styles.sectionTitle}>{ui('responseFields')}:</h2>
              <DocTable table={fields} data-testid="dp-doc-fields" />
            </section>
          )}
          {headers && (
            <section className={styles.section} id={headersId}>
              <h2 className={styles.sectionTitle}>{ui('responseHeaders')}:</h2>
              <DocTable table={headers} data-testid="dp-doc-headers" />
            </section>
          )}
        </>
      }
      aside={
        <>
          <CodeBlock
            title={resource.requestTitle}
            code={resource.requestExample ?? `${resource.method} ${resource.path}`}
            language={resource.requestExample ? 'bash' : 'http'}
            lineNumbers
            wrap
            data-testid="dp-doc-request-example"
          />
          {resource.responseExample && (
            <CodeBlock
              title={t('developerPlatform.docs.ui.responseExample', {
                status: resource.responseExample.status
              })}
              code={resource.responseExample.code}
              language="json"
              lineNumbers
              wrap
              data-testid="dp-doc-response-example"
            />
          )}
        </>
      }
      data-testid={testId}
    />
  )
}
